import type { AccountsUsageInfo } from '@customerio/hydra-ui-api';

type UsageEnvironmentInput = Pick<
  NonNullable<AccountsUsageInfo['environment_usages']>[number],
  | 'environment_id'
  | 'customers_total'
  | 'customers_churn'
  | 'objects_total'
  | 'messages_sent'
  | 'billable_messages_sent'
  | 'updated_at'
  | 'billed'
>;

type BillingUsageInput = Pick<
  AccountsUsageInfo,
  | 'customers_total'
  | 'objects_total'
  | 'messages_sent'
  | 'billable_messages_sent'
  | 'high_watermark_customers_total'
  | 'high_watermark_objects_total'
  | 'builder_credit_cost_per_message'
  | 'includes_all_envs'
  | 'customers_allotted'
  | 'messages_allotted'
  | 'objects_allotted'
  | 'monthly_active_users'
> & {
  environment_usages?: UsageEnvironmentInput[];
};

/** One workspace's metered usage. */
export interface BillingUsageEnvironment {
  environmentId: number;
  customersTotal: number;
  /**
   * Profiles removed this period, as counted against the high watermark. On a watermark-billed plan
   * the billable count is `customersTotal + customersChurn`, since a deletion does not lower it.
   */
  customersChurn: number;
  objectsTotal: number;
  /** Emails sent, billed or not. A send through the account's own mail server is not billed. */
  messagesSent: number;
  /** The subset of `messagesSent` that counts toward the plan. */
  billableMessagesSent: number;
  updatedAt: number;
  billed: boolean;
}

/** Account usage, as the billing service exposes it. */
export interface BillingUsage {
  customersTotal: number;
  objectsTotal: number;
  /** Emails sent, billed or not. A send through the account's own mail server is not billed. */
  messagesSent: number;
  /** The subset of `messagesSent` that counts toward the plan. */
  billableMessagesSent: number;
  highWatermarkCustomersTotal: number;
  highWatermarkObjectsTotal: number;
  monthlyActiveUsers: number;
  builderCreditCostPerMessage: number;
  includesAllEnvironments: boolean;
  customersAllotted: number;
  messagesAllotted: number;
  objectsAllotted: number;
  environments: BillingUsageEnvironment[];
}

/**
 * Whether a workspace is unbilled, which makes the account-level totals an undercount — they sum only
 * billed workspaces, while `environments` holds every one.
 */
export function hasUnbilledEnvironment(environments: BillingUsageEnvironment[]): boolean {
  return environments.some((environment) => !environment.billed);
}

/**
 * Normalizes a usage response. Both `usage` and `usage_v2` return the same struct, so one transform
 * serves both.
 *
 * `monthly_active_users` is the only field carrying `omitempty`, and only `usage_v2` assigns it, so it is
 * absent from every legacy response and reads as 0 there — matching the Ember billing page.
 */
export function toBillingUsage(usage: BillingUsageInput): BillingUsage {
  return {
    customersTotal: usage.customers_total,
    objectsTotal: usage.objects_total,
    messagesSent: usage.messages_sent,
    billableMessagesSent: usage.billable_messages_sent,
    highWatermarkCustomersTotal: usage.high_watermark_customers_total,
    highWatermarkObjectsTotal: usage.high_watermark_objects_total,
    monthlyActiveUsers: usage.monthly_active_users ?? 0,
    builderCreditCostPerMessage: usage.builder_credit_cost_per_message,
    includesAllEnvironments: usage.includes_all_envs,
    customersAllotted: usage.customers_allotted,
    messagesAllotted: usage.messages_allotted,
    objectsAllotted: usage.objects_allotted,
    environments: (usage.environment_usages ?? []).map((environment) => ({
      environmentId: environment.environment_id,
      customersTotal: environment.customers_total,
      customersChurn: environment.customers_churn,
      objectsTotal: environment.objects_total,
      messagesSent: environment.messages_sent,
      billableMessagesSent: environment.billable_messages_sent,
      updatedAt: environment.updated_at,
      billed: environment.billed,
    })),
  };
}
